import { LitElement, html, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { keyed } from 'lit/directives/keyed.js'
import { consume } from '@lit/context'
import { style } from 'components/shared/product-tile-configurator/components/tile-configurator-sidebar.style.js'
import { areaCrumbFor, panelHeaderStyle, renderPanelHeader, renderPanelTabs, viewCrumb, zoneCrumbFor, type PanelTab } from './panel-header.js'
import type { WidgetCondition, WidgetConditionCfg } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { WIDGET_CONDITIONS } from 'components/shared/base-product-tile/widget-rules.js'
import { BUILT_IN_CONDITIONS, BUILT_IN_VISIBILITY, BUILT_IN_VISIBILITY_SHORT, CONDITION_LABELS, DISPLAY_NAMES, describeCondition, hasVisibilityConditions } from 'components/shared/product-tile-configurator/widget-metadata.js'
import { WIDGET_PROPERTY_SCHEMAS, type PropertyFieldSchema } from 'components/shared/product-tile-configurator/widget-property-schema.js'
import './tile-property-field.js'
import type { PropertyChangeDetail } from './tile-property-field.js'
import { type ConfigMode, type Section, type Widget } from 'components/shared/product-tile-configurator/editor-state.js'
import {
  CanvasStateContext,
  MobileModeContext,
  SelectedWidgetContext,
  TileConfiguratorActionsContext,
  type CanvasStateContextType,
  type MobileModeContextType,
  type SelectedWidgetContextType,
  type TileConfiguratorActionsContextType,
} from 'components/shared/product-tile-configurator/context.js'

type WidgetTab = 'settings' | 'visibility'

const CONDITION_STATES: Array<{ value: boolean; label: string }> = [
  { value: true, label: 'is' },
  { value: false, label: 'is not' },
]

const isWidgetCondition = (value: string): value is WidgetCondition => (WIDGET_CONDITIONS as readonly string[]).includes(value)

const widgetTabs = (hasVisibilityConditions: boolean): PanelTab<WidgetTab>[] => [
  { value: 'settings', label: 'Settings' },
  { value: 'visibility', label: 'Visibility', indicator: hasVisibilityConditions ? 'Has visibility conditions' : undefined },
]

const groupSchemas = (schemas: PropertyFieldSchema[]): Array<[string, PropertyFieldSchema[]]> => {
  const groups = new Map<string, PropertyFieldSchema[]>()
  schemas.forEach(schema => {
    const groupName = schema.group ?? 'Properties'
    const existing = groups.get(groupName)
    if (existing) {
      existing.push(schema)
    } else {
      groups.set(groupName, [schema])
    }
  })
  return Array.from(groups.entries())
}

@customElement('csc-tile-configurator-sidebar')
export class CSCTileConfiguratorSidebar extends LitElement {
  static override styles = [panelHeaderStyle, style]

  @consume({ context: CanvasStateContext, subscribe: true })
  private sectionsByMode!: CanvasStateContextType

  @consume({ context: SelectedWidgetContext, subscribe: true })
  private selectedZone!: SelectedWidgetContextType

  @consume({ context: MobileModeContext, subscribe: true })
  private mobileMode!: MobileModeContextType

  @consume({ context: TileConfiguratorActionsContext })
  private actions!: TileConfiguratorActionsContextType

  // Property groups are collapsible sections, all expanded by default; collapsing one is
  // remembered per widget (id + group name) for the session, so re-selecting it keeps the state.
  @state()
  private collapsedGroups = new Set<string>()

  // Stays on the last viewed tab when a different widget is selected.
  @state()
  private activeTab: WidgetTab = 'settings'

  private toggleGroup(groupKey: string) {
    const next = new Set(this.collapsedGroups)
    if (next.has(groupKey)) next.delete(groupKey)
    else next.add(groupKey)
    this.collapsedGroups = next
  }

  private getSelectedWidget(): { mode: ConfigMode; widget: Widget; section: Section } | null {
    const selectedZone = this.selectedZone
    if (!selectedZone?.widgetId) return null
    const section = this.sectionsByMode[selectedZone.mode].find(s => s.id === selectedZone.sectionId)
    const widget = section?.widgets.find(w => w.id === selectedZone.widgetId)
    return widget && section ? { mode: selectedZone.mode, widget, section } : null
  }

  private handlePropertyChange(mode: ConfigMode, widgetId: string, event: CustomEvent<PropertyChangeDetail>) {
    this.actions.updateWidgetProperty(mode, widgetId, event.detail.key, event.detail.value)
  }

  private unusedConditions(widget: Widget): WidgetCondition[] {
    const builtIn = BUILT_IN_CONDITIONS[widget.type] ?? []
    const showWhen = widget.showWhen ?? []
    return WIDGET_CONDITIONS.filter(condition => !builtIn.includes(condition) && !showWhen.some(entry => entry.condition === condition))
  }

  private addCondition(mode: ConfigMode, widget: Widget) {
    const condition = this.unusedConditions(widget)[0]
    if (!condition) return
    this.actions.updateWidgetShowWhen(mode, widget.id, [...(widget.showWhen ?? []), { condition, is: true }])
  }

  private removeCondition(mode: ConfigMode, widget: Widget, index: number) {
    this.actions.updateWidgetShowWhen(mode, widget.id, (widget.showWhen ?? []).filter((_, i) => i !== index))
  }

  private updateCondition(mode: ConfigMode, widget: Widget, index: number, change: Partial<WidgetConditionCfg>) {
    this.actions.updateWidgetShowWhen(mode, widget.id, (widget.showWhen ?? []).map((entry, i) => i === index ? { ...entry, ...change } : entry))
  }

  private setCondition(mode: ConfigMode, widget: Widget, index: number, condition: string) {
    if (!isWidgetCondition(condition) || !this.unusedConditions(widget).includes(condition)) return
    this.updateCondition(mode, widget, index, { condition })
  }

  // cse-segmented-control has no value property — it always activates its first item on connect
  // (and fires a change event doing so). So the selected item is driven from here instead: after
  // every render, activate each condition's item matching its is / is not. Changes come in through
  // the items' own click listeners, never the control's change event, so this programmatic
  // activation can't feed back into the conditions.
  protected override updated(): void {
    const showWhen = this.getSelectedWidget()?.widget.showWhen ?? []
    this.renderRoot.querySelectorAll<HTMLElement & { selectedIndex: number; activateControlItem?: (index: number) => void }>('.visibility-condition-state').forEach(control => {
      const entry = showWhen[Number(control.dataset['index'])]
      const index = CONDITION_STATES.findIndex(option => option.value === entry?.is)
      if (index >= 0 && control.selectedIndex !== index) control.activateControlItem?.(index)
    })
  }

  override render() {
    const selected = this.getSelectedWidget()
    // Shares the right panel with csc-tile-configurator-zone-panel, which renders area/zone
    // settings and the empty state — this one only ever renders for a selected widget.
    if (!selected) return nothing

    const { mode, widget, section } = selected
    const schemas = WIDGET_PROPERTY_SCHEMAS[widget.type] ?? []
    const groups = groupSchemas(schemas)
    const builtInVisibility = BUILT_IN_VISIBILITY[widget.type]
    const builtInShort = BUILT_IN_VISIBILITY_SHORT[widget.type]
    const builtInCondition = BUILT_IN_CONDITIONS[widget.type]?.[0]
    const builtInLabel = builtInCondition ? CONDITION_LABELS[builtInCondition] : builtInShort ? builtInShort.charAt(0).toUpperCase() + builtInShort.slice(1) : undefined
    const showWhen = widget.showWhen ?? []

    return html`
      ${renderPanelHeader(
        [viewCrumb(mode, this.mobileMode), areaCrumbFor(mode, section, this.actions), zoneCrumbFor(mode, section, this.actions)],
        DISPLAY_NAMES[widget.type] ?? widget.type,
        'widget',
        renderPanelTabs(widgetTabs(hasVisibilityConditions(widget)), this.activeTab, 'widget', tab => { this.activeTab = tab }),
      )}

      ${this.activeTab === 'visibility' ? html`
        <div id="widget-panel-visibility" class="visibility-panel" role="tabpanel" aria-labelledby="widget-tab-visibility">
          ${keyed(widget.id, html`
            <div class="visibility-rules">
              <cse-text class="property-group-title" variant="caption-s">Widget is visible when</cse-text>
              ${builtInVisibility && builtInLabel ? html`
                <cse-tooltip class="visibility-rule-tooltip" position="top">
                  <span class="visibility-rule-tooltip-text">Built into this widget, can't be changed: ${builtInVisibility}</span>
                  <div slot="anchor" class="visibility-rule visibility-built-in">
                    <span class="visibility-built-in-lock" role="img" aria-label="Built-in, can't be changed"><cse-icon icon-name="lock" icon-style="solid"></cse-icon></span>
                    <div class="visibility-rule-header">
                      <cse-segmented-control class="visibility-built-in-state">
                        ${CONDITION_STATES.map(option => html`
                          <cse-segmented-control-item value=${String(option.value)} disabled>${option.label}</cse-segmented-control-item>
                        `)}
                      </cse-segmented-control>
                    </div>
                    <cse-select class="visibility-built-in-condition" disabled aria-label="Built-in condition" value="built-in">
                      <cse-option value="built-in" selected>${builtInLabel}</cse-option>
                    </cse-select>
                  </div>
                </cse-tooltip>
              ` : nothing}
              ${showWhen.length ? showWhen.map((entry, index) => html`
                ${index > 0 || builtInVisibility ? html`<cse-text class="property-group-title visibility-rules-and" variant="caption-s">AND</cse-text>` : nothing}
                <cse-tooltip class="visibility-rule-tooltip" position="top">
                  <span class="visibility-rule-tooltip-text">${describeCondition(entry)}</span>
                  <div slot="anchor" class="visibility-rule">
                    <button
                      type="button"
                      class="visibility-rule-remove"
                      aria-label="Remove condition"
                      @click=${() => this.removeCondition(mode, widget, index)}>
                      <cse-icon icon-name="xmark" icon-style="solid"></cse-icon>
                    </button>
                    <div class="visibility-rule-header">
                      ${keyed(`${widget.id}-${index}-${showWhen.length}`, html`
                        <cse-segmented-control class="visibility-condition-state" data-index=${index}>
                          ${CONDITION_STATES.map(option => html`
                            <cse-segmented-control-item
                              value=${String(option.value)}
                              @click=${() => this.updateCondition(mode, widget, index, { is: option.value })}>
                              ${option.label}
                            </cse-segmented-control-item>
                          `)}
                        </cse-segmented-control>
                      `)}
                    </div>
                    <cse-select
                      class="visibility-condition"
                      required
                      aria-label="Condition"
                      value=${entry.condition}
                      @change=${(e: Event) => this.setCondition(mode, widget, index, (e.target as HTMLSelectElement).value)}>
                      ${[entry.condition, ...this.unusedConditions(widget)].sort((a, b) => WIDGET_CONDITIONS.indexOf(a) - WIDGET_CONDITIONS.indexOf(b)).map(condition => html`
                        <cse-option value=${condition} ?selected=${condition === entry.condition}>${CONDITION_LABELS[condition]}</cse-option>
                      `)}
                    </cse-select>
                  </div>
                </cse-tooltip>
              `) : builtInVisibility ? nothing : html`
                <cse-text class="visibility-always" variant="body-s">Always visible. Add a condition to only show this widget for certain products.</cse-text>
              `}
              ${this.unusedConditions(widget).length ? html`
                <cse-button class="visibility-add-rule" variant="default" size="small" @click=${() => this.addCondition(mode, widget)}>
                  <cse-icon icon-name="plus" icon-style="light"></cse-icon>
                  Add condition
                </cse-button>
              ` : nothing}
            </div>
          `)}
        </div>
      ` : html`
      <div id="widget-panel-settings" role="tabpanel" aria-labelledby="widget-tab-settings">
      ${schemas.length === 0 ? html`
        <div class="property-fields">
          <cse-text variant="body-s">No configurable properties for this widget.</cse-text>
        </div>
      ` : html`
        <div class="property-groups">
          ${groups.map(([groupName, fields]) => {
            const groupKey = `${widget.id}:${groupName}`
            const expanded = !this.collapsedGroups.has(groupKey)
            const contentId = `property-group-${groupName.replace(/\W+/g, '-').toLowerCase()}`
            return html`
              <section class="property-group ${expanded ? 'expanded' : ''}">
                <button
                  type="button"
                  class="property-group-toggle"
                  aria-expanded=${expanded ? 'true' : 'false'}
                  aria-controls=${contentId}
                  @click=${() => this.toggleGroup(groupKey)}>
                  <cse-text class="property-group-title" variant="caption-s">${groupName}</cse-text>
                  <cse-icon icon-name=${expanded ? 'caret-up' : 'caret-down'} icon-style="solid"></cse-icon>
                </button>
                <div id=${contentId} class="property-fields" ?hidden=${!expanded}>
                  ${fields.map(schema => keyed(`${widget.id}-${schema.key}`, html`
                    <csc-tile-property-field
                      .schema=${schema}
                      .properties=${widget.properties}
                      @property-change=${(e: CustomEvent<PropertyChangeDetail>) => this.handlePropertyChange(mode, widget.id, e)}>
                    </csc-tile-property-field>
                  `))}
                </div>
              </section>
            `
          })}
        </div>
      `}
      </div>
      `}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-configurator-sidebar': CSCTileConfiguratorSidebar
  }
}
